import { NetworkStatus, WatchQueryFetchPolicy } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { initMetadataV2 } from '@modules/dashboard/hooks/useFetchDashboard/helpers/initMetadataV2';
import { useInitDashboardQuery } from '@modules/dashboard/hooks/useInitDashboardQuery';
import {
  DashboardViewFragment,
  useGetDashboardByTokenQuery,
} from 'generated/graphql';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { useGetUser } from 'v2/core/services/user';
import { DashboardActionType } from '../../store/dashboardActionType';
import { useDashboardStore } from '../../store/useDashboardStore';

interface useFetchDashboardParams {
  token?: string;
  skip?: boolean;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onLoadDashboard?: (dashboard: DashboardViewFragment) => void;
}

export const useFetchDashboard = ({
  skip = false,
  token,
  nextFetchPolicy = 'cache-first',
  onLoadDashboard: onLoadDashboardProp,
}: useFetchDashboardParams) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );

  const onLoadDashboard = (dashboard: DashboardViewFragment) => {
    if (!dashboard || dashboard.viewToken !== token) {
      return;
    }

    dispatch({
      type: DashboardActionType.SET_DASHBOARD,
      payload: dashboard,
    });

    // Avoid updating metadata on every save. It creates data inconsistency issues.
    initMetadataV2({
      dashboard,
      dispatch,
      cellMinimumHeightsByRowID,
    });

    onLoadDashboardProp?.(dashboard);
  };

  const { data, error, networkStatus, loading, startPolling, stopPolling } =
    useGetDashboardByTokenQuery({
      variables: {
        token: token ?? '',
        tenantID,
        userID,
        includeScheduleOutbounds: true,
      },
      skip,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      nextFetchPolicy,
      onCompleted: (data) =>
        data.getDashboardByToken && onLoadDashboard(data.getDashboardByToken),
    });

  const dashboard = data?.getDashboardByToken;

  useInitDashboardQuery(dashboard?.filterClause);

  useEffect(() => {
    if (!error) {
      return;
    }

    const isRecordNotFoundError = isErrorCode(error, ErrorCode.RecordNotFound);
    if (isRecordNotFoundError) {
      showErrorNotification({
        contentComponent: 'Invalid Dashboard Link',
        props: {},
      });
      return;
    }

    showErrorNotification({
      contentComponent: 'Failed to fetch the dashboard',
      props: {},
    });
  }, [error, showErrorNotification]);

  const polling = networkStatus === NetworkStatus.poll;

  useEffect(() => {
    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        isPolling: polling,
      },
    });
  }, [loading, polling, dispatch]);

  return {
    dashboard: isEmpty(dashboard) ? null : dashboard,
    loading,
    polling,
    error,
    startPolling,
    stopPolling,
  };
};
